/**
 * 68.5, one member's direct order (Ghost Recon's; the owner's frame): the order menu's inputs for a squad, given to one
 * member of it instead. Each order becomes a **task naming that unit** (a member's order is a task, never a posture: a
 * base order belongs to a squad), lasting DIRECT_ORDER_SECONDS — it overrides the squad's order and its leader's step for
 * that member until it ends: its time runs out, its form lapses (an engage's target down or lost, a crew's seat gone —
 * core/orderForms.ts), or the squad is given a newer task. A fire toggle goes to that member alone. The squad-wide forms —
 * a formation, a tactic (it needs two halves) — are refused; a mark and a call for fire stay the team's (they are
 * knowledge and a request, not the member's order). Pure: the host applies the inputs as it does the squad's.
 */
import type { GameState, MatchInputBody } from '../types';
import type { MenuInput } from './orderMenu';

/** How long a member's direct order stands (s) before the member goes back to its squad's order. */
export const DIRECT_ORDER_SECONDS = 60;

type OrderBody = Extract<MatchInputBody, { kind: 'order' }>;

/**
 * The inputs for `unitId` alone, from the menu's inputs for its squad; `tick` the paused or current action tick, `dt`
 * the tick's length. Refused when nothing in them can go to one member.
 */
export function toMember(inputs: readonly MenuInput[], unitId: number, tick: number, dt: number): { inputs: MenuInput[] } | { refused: string } {
  const out: MenuInput[] = [];
  const seen = new Set<string>();
  const expires = tick + Math.round(DIRECT_ORDER_SECONDS / dt);
  for (const body of inputs) {
    if (body.kind === 'toggle') { if (body.unitId === unitId) out.push(body); continue; }
    const o = (body as OrderBody).order;
    if (!('kind' in o)) continue;
    if (o.kind === 'formation') return { refused: 'A formation is the squad\'s, not one member\'s' };
    if (o.kind === 'bound' || o.kind === 'flank') return { refused: 'A tactic needs the squad\'s two halves' };
    if (o.kind === 'mark' || o.kind === 'callIn') { out.push(body); continue; }   // the team's knowledge, a request for fire
    const key = JSON.stringify(o);
    if (seen.has(key)) continue;   // (one squad's inputs: one order)
    seen.add(key);
    out.push({ kind: 'order', team: (body as OrderBody).team, to: { units: [unitId] }, tier: 'task', order: o, expires });
  }
  return out.length ? { inputs: out } : { refused: 'Nothing for one member there' };
}

/** Fire at will / hold fire for one member: `sub` as the menu's arc ('fire', 'holdFire'), null flips it. Empty when it is so already. */
export function memberFireToggle(state: Pick<GameState, 'players'>, unitId: number, sub: string | null): MenuInput[] {
  const u = state.players.find(p => p.id === unitId);
  if (!u || !u.alive) return [];
  const holding = sub === 'holdFire' ? true : sub === 'fire' ? false : !u.holdFire;
  return !!u.holdFire === holding ? [] : [{ kind: 'toggle', unitId, toggle: 'holdFire' }];
}
